import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/components/ui/icon';
import { toast } from "@/hooks/errors/use-toast";
import { supabase } from '@/integrations/supabase/client';
import AuthLayout from '@/components/auth/AuthLayout';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "error",
      });
      return;
    }
    await handlePasswordReset();
  };

  const handlePasswordReset = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Reset Email Sent",
        description: "If an account with that email exists, we've sent you a password reset link.",
        variant: "success",
      });

      // Navigate back to sign in after showing success message
      setTimeout(() => {
        navigate('/signin');
      }, 3000);

    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AuthLayout
        title="Forgot Password?"
        description="Enter your email address and we'll send you a link to reset your password"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Email Address
            </Label>
            <div className="relative">
              <Input 
                id="email" 
                type="email" 
                placeholder="Enter your email address" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-11 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 focus-visible:border-accent/40 hover:border-accent/30 hover:shadow-md transition-all duration-300 shadow-sm"
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
              <Icon 
                name="Mail" 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent" 
                size={18} 
              />
            </div>
          </div>

          <Button 
            type="submit"
            disabled={isSubmitting || !email}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-medium py-2.5"
          >
            {isSubmitting ? (
              <>
                <Icon name="Loader2" className="mr-2 h-4 w-4 animate-spin" />
                Sending Reset Email...
              </>
            ) : (
              <>
                Send Reset Email
                <Icon name="Mail" className="ml-2" size={16} />
              </>
            )}
          </Button>

          <div className="text-center">
            <Button 
              variant="link" 
              className="p-0 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300" 
              onClick={() => navigate('/signin')}
              type="button"
            >
              ← Back to Sign In
            </Button>
          </div>
        </form>

      </AuthLayout>
    </motion.div>
  );
};

export default ForgotPassword; 